import { Icon } from "@iconify/react";
import DeleteIcon from "@mui/icons-material/Delete";
import { Dialog } from "@mui/material";
import { ChartData } from "chart.js";
import { format } from "date-fns";
import { useFormik } from "formik";
import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import * as Yup from "yup";
import AlertDialog from "../../components/shared/AlertDialog";
import { CustomFieldsSection } from "../../components/shared/CustomFieldsSection";
import { UdfEntityType } from "../../types/CustomFields";
import FileInput from "../../components/shared/FileInput";
import { Input, Label } from "../../components/shared/InputField";
import LazyPdfDocument from "../../components/shared/LazyPdfDocument";
import SearchDropDown from "../../components/shared/SearchDropdown";
import { Select } from "../../components/shared/Select";
import {
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  Error,
  HStack,
  VStack,
} from "../../components/utils";
import { useEmployees } from "../../queries/employees";
import {
  useAddGrant,
  useEsopPlans,
  useGetVestingOverrides,
} from "../../queries/esopPlan";
import { useVestingTemplates } from "../../queries/vestingTemplate";
import { useCompanyStore } from "../../store";
import { useRegionalGrantRules } from "../../queries/onBoarding";
import { useError } from "../../store/errorStore";
import { useGrantCreatedDialog } from "../../store/useDialogStore";
import { Employee, EmployementStatus } from "../../types/Employee";
import { EsopPlan } from "../../types/EsopPlan";
import { AddGrantReq, Grant, VestingDateType } from "../../types/Grant";
import { VestingType } from "../../types/VestingTemplate";
import { sort } from "../../utils/arrays";
import convertToBase64 from "../../utils/convertToBase64";
import {
  getCurrencySymbol,
  getCurrencyType,
} from "../../utils/currencyFormatter";
import { formatWithTimeZone, getActualVestingDay } from "../../utils/date";
import canUserBeAssignedToThisPlan from "../../utils/grantRule";
import { resolveRegionalGrantRule } from "../../utils/regionalGrantRules";
import { getFormattedValue } from "../../utils/string";
import { BarChart } from "../vestingSchedules/BarChart";
import { generateProjections } from "../vestingSchedules/generateProjections";
import {
  getServerErrorMessage,
  getServerValidationErrors,
  isValidStandardInput,
  standardInputMessage,
} from "../../utils/standardInputValidation";

type AddOrEditGrantProps = {
  mode?: "Edit" | "Clone" | "Add" | "Delete";
  onClose: () => void;
  grant?: Grant;
  onSaveSuccess?: () => void;
};

export function AddOrEditGrant(props: AddOrEditGrantProps) {
  const { companyData } = useCompanyStore();
  const { data: regionalRuleCatalog } = useRegionalGrantRules();
  const regionalRule = resolveRegionalGrantRule(
    regionalRuleCatalog?.rules,
    companyData?.companyMetaData?.registeredCountry
  );
  const currency = getCurrencyType();
  const currencySymbol = getCurrencySymbol();
  const { id } = useParams();
  const _id = id ?? "";
  const { setState: setGrantCreatedDialog } = useGrantCreatedDialog();
  const errorMessage = useError();
  const { mode, onClose, grant, onSaveSuccess } = props;
  const [showDialog, setShowDialog] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<
    Record<string, any>
  >(grant?.customFieldValues ?? {});
  const [grantByAmount, setGrantByAmount] = useState(false);
  const [sharePrice, setSharePrice] = useState(1);
  const [amountGranted, setAmountGranted] = useState(0);
  const { data: _employees } = useEmployees();
  const { data: availableVestingOverrides } = useGetVestingOverrides();
  const employees = sort(_employees || [], "employeeName");
  const employedEmployees = employees.filter(
    (employee) => employee.employmentStatus === EmployementStatus.EMPLOYED
  );
  const [selectedEmployee, setSelectedEmployee] = useState<Employee>();
  const { data: _plans } = useEsopPlans();
  const [selectedPlan, setSelectedPlan] = useState<EsopPlan>();
  const isStrikePriceOptional =
    selectedPlan?.planType === "ESOP" &&
    regionalRule.strikePriceOptional !== undefined;
  const plans = _plans || [];
  const availableActualVestingDays: string[] = useMemo(
    () => availableVestingOverrides?.data,
    [availableVestingOverrides]
  );
  let singlePlanName: string = "";
  if (_id !== "") {
    const individualPlan = plans.filter((plan) => plan.esopPlanId === _id)[0];
    singlePlanName = individualPlan?.planName;
  }
  const { data: vestingTemplates } = useVestingTemplates();
  const vestingScheduleTemplates = vestingTemplates?.filter(
    (schedule) => !schedule.isDefault
  );
  const _vestingTemplateNames = vestingScheduleTemplates?.map(
    (v) => v.vestingTemplateName
  );
  const vestingTemplateNames = _vestingTemplateNames || [];
  const vestingTypes = [
    {
      description: "Vesting From date of Grant",
      value: VestingDateType.GRANT_DATE,
    },
    {
      description: "Vesting From date of Joining",
      value: VestingDateType.EMPLOYEE_JOINING_DATE,
    },
    { description: "Custom Vesting", value: VestingDateType.CUSTOM_DATE },
  ];
  const employeeJoiningDate = new Date(
    selectedEmployee?.dateOfJoin || "1900-01-02"
  );
  employeeJoiningDate.setDate(employeeJoiningDate.getDate() - 1);
  employeeJoiningDate.setHours(23);
  const minStartDate =
    new Date(selectedPlan?.planStartDate || "1900-01-02") >
    new Date(selectedEmployee?.dateOfJoin || "1900-01-02")
      ? new Date(selectedPlan?.planStartDate || "1900-01-02")
      : new Date(selectedEmployee?.dateOfJoin || "1900-01-02");
  minStartDate.setDate(minStartDate.getDate() - 1);
  minStartDate.setHours(23);
  const baseValues = {
    employeeId: "",
    employeeName: "",
    employeeIdentificationString: "",
    planName: singlePlanName ?? "",
    planId: "",
    planType: "",
    vestingDateType: VestingDateType.GRANT_DATE,
    vestingTemplate: "",
    vestingId: "",
    optionsGranted: 0,
    grantPrice: 0,
    dateOfGrant: format(new Date(), "yyyy-MM-dd"),
    vestingDate: format(new Date(), "yyyy-MM-dd"),
    grantLetterType: "",
    grantLetter: "",
    grantLetterFileName: "Attach File",
    customizedGrantLetter: false,
    actualVestingDay: "SAME_DAY",
  };
  function getInitialValuesForEditing(grant: Grant) {
    return {
      id: grant?.optionHolderId,
      employeeId: grant.employeeId,
      employeeName: grant.optionHolderName,
      employeeIdentificationString:
        employees.find((emp) => emp.id === grant.employeeId)
          ?.employeeIdentificationString ?? "",
      planId: grant.planid ?? "",
      planType: grant.planType,
      planName: grant.planName,
      vestingDateType: grant.vestingDateType,
      vestingTemplate: grant.vestingTemplateName,
      vestingId: grant.vestingId,
      optionsGranted: grant.optionsGranted,
      grantPrice: grant.grantPrice,
      dateOfGrant: format(new Date(grant.grantDate), "yyyy-MM-dd"),
      vestingDate: format(new Date(grant.vestingDate), "yyyy-MM-dd"),
      grantLetterFileName: "",
      customizedGrantLetter: grant.customizedGrantLetter,
      actualVestingDay: grant.actualVestingDay,
    };
  }
  function getInitialValuesForCloning(grant: Grant) {
    return {
      employeeId: "",
      employeeName: "",
      employeeIdentificationString: "",
      planName: grant.planName,
      planId: grant.planid ?? "",
      planType: grant.planType,
      vestingDateType: grant.vestingDateType,
      vestingTemplate: grant.vestingTemplateName,
      vestingId: grant.vestingId,
      optionsGranted: grant.optionsGranted,
      grantPrice: grant.grantPrice,
      dateOfGrant: format(new Date(grant.grantDate), "yyyy-MM-dd"),
      vestingDate: format(new Date(grant.vestingDate), "yyyy-MM-dd"),
      grantLetterFileName: "Attach File",
      customizedGrantLetter: false,
      actualVestingDay: grant.actualVestingDay,
    };
  }
  function getInitialValues() {
    if (mode === "Edit" && grant) {
      return getInitialValuesForEditing(grant);
    } else if (mode === "Clone" && grant) {
      return getInitialValuesForCloning(grant);
    } else {
      return baseValues;
    }
  }

  const allowedOptions =
    mode === "Edit"
      ? (selectedPlan?.optionsReserved || 0) + (grant?.optionsGranted || 0) || 0
      : selectedPlan?.optionsReserved || 0;
  const isSelectedPlanFractional =
    (selectedPlan && selectedPlan?.isFractional) || false;
  const validationSchema = Yup.object().shape({
    employeeId: Yup.string()
      .required("Select the Option Holder")
      .notOneOf([""], "required"),
    employeeName: Yup.string().test(
      "safe-employee-name",
      standardInputMessage("Employee name"),
      (value) => isValidStandardInput(value)
    ),
    planName: Yup.string()
      .required("Select the Plan Name")
      .test("safe-plan-name", standardInputMessage("Plan name"), (value) =>
        isValidStandardInput(value)
      ),
    vestingDateType: Yup.string().oneOf(vestingTypes.map((v) => v.value)),
    optionsGranted: Yup.number()
      .max(
        allowedOptions,
        `Cannot issue more than ${allowedOptions} available options in this plan`
      )
      .moreThan(0, "Minimum one option is required")
      .required("Options to be granted is required")
      .test("is-fractional", "Options must be an integer", (value) => {
        if (isSelectedPlanFractional) {
          return true;
        } else {
          return Number.isInteger(value);
        }
      }),
    planType: Yup.string(),
    grantPrice: Yup.number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .test(
        "grant-price-required",
        "Grant Price is required",
        (value) =>
          isStrikePriceOptional || (value !== undefined && value !== null)
      )
      // .test(
      //   "is-valid-grant-price",
      //   "Grant price should be more than 0",
      //   (value, { parent }) => {
      //     const { planType } = parent as AddGrantReq;
      //     if (value === 0 && planType !== "RSU") {
      //       return false;
      //     }
      //     return true;
      //   }
      // )
      .min(0, "Grant price cannot be less than 0")
      .test(
        "regional-strike-price-required",
        `Grant Price is required for ${regionalRule.country}`,
        (value) => {
          if (value === undefined || value === null) return true;
          if (selectedPlan?.planType === "RSU") return value >= 0.0001;
          if (selectedPlan?.planType !== "ESOP") return value >= 0;
          return isStrikePriceOptional || value > 0;
        }
      )
      .test(
        "regional-grant-price-minimum",
        `Grant Price must be at least ${regionalRule.minStrikePrice}`,
        (value) =>
          value === undefined ||
          value === null ||
          selectedPlan?.planType !== "ESOP" ||
          isStrikePriceOptional ||
          regionalRule.minStrikePrice === undefined ||
          value >= regionalRule.minStrikePrice
      ),
    vestingTemplate: Yup.string()
      .required("Select the Vesting Schedule")
      .oneOf(
        vestingTemplateNames.map((v) => v.toString()),
        "invalid vesting template"
      ),
    vestingDate: Yup.date()
      .required("Required")
      .min(
        employeeJoiningDate,
        `Vesting date should not be before employee joining date (
        ${format(
          new Date(selectedEmployee?.dateOfJoin || "1900-01-01"),
          "dd-MM-yyyy"
        )})`
      ),
    dateOfGrant: Yup.date()
      .required("Required")
      .min(
        minStartDate,
        `Grant date should not be before plan start date (
      ${format(
        new Date(selectedPlan?.planStartDate || "1900-01-01"),
        "dd-MM-yyyy"
      )}) and employee join date (
        ${format(
          new Date(selectedEmployee?.dateOfJoin || "1900-01-01"),
          "dd-MM-yyyy"
        )})`
      ),
    actualVestingDay: Yup.string().oneOf(availableActualVestingDays || []),
  });
  const { mutate: addGrant, isLoading, status: addGrantStatus } = useAddGrant();
  const {
    mutate: editGrant,
    isLoading: editLoading,
    status: editGrantStatus,
  } = useAddGrant();

  const [documentDialog, setDocumentDialog] = useState<{
    open: boolean;
    grant?: Grant;
  }>({ open: false, grant: undefined });
  const formik = useFormik<AddGrantReq>({
    initialValues: getInitialValues(),
    enableReinitialize: true,
    validationSchema,
    onSubmit: () => {
      const grantDto = { ...formik.values, customFieldValues };
      grantDto.planId =
        plans.find((plan) => plan.planName === grantDto.planName)?.esopPlanId ||
        "";
      grantDto.vestingId =
        vestingScheduleTemplates?.find(
          (template) =>
            template.vestingTemplateName === grantDto.vestingTemplate
        )?.id || "";
      grantDto.vestingDate = formatWithTimeZone(grantDto.vestingDate);
      grantDto.dateOfGrant = formatWithTimeZone(grantDto.dateOfGrant);
      setShowDialog(true);
      if (mode === "Edit") {
        editGrant(grantDto, {
          onSuccess: () => {
            formik.resetForm();
            toast("Grant Edited Successfully!", { type: "success" });
            onClose();
            onSaveSuccess?.();
          },
          onError: (err: any) => {
            formik.setErrors(getServerValidationErrors(err));
            const message = getServerErrorMessage(err);
            errorMessage.setMessage(message);
            toast(message, { type: "error", autoClose: 2000 });
          },
        });
      } else {
        addGrant(grantDto, {
          onSuccess: (result) => {
            formik.resetForm();
            setGrantCreatedDialog({
              open: true,
              grantId: result.data.optionHolderId,
              variant: "created",
            });
            onClose();
            toast("Grant Added Successfully!", { type: "success" });
          },
          onError: (err: any) => {
            formik.setErrors(getServerValidationErrors(err));
            const message = getServerErrorMessage(err);
            errorMessage.setMessage(message);
            toast(message, {
              type: "error",
              autoClose: 2000,
            });
          },
        });
      }
    },
  });
  useEffect(() => {
    if (mode === "Add" && _id !== "") {
      const individualPlan = plans.find((plan) => plan.esopPlanId === _id);
      formik.setFieldValue("planName", individualPlan?.planName);
      formik.setFieldValue("planType", individualPlan?.planType);
      setSelectedPlan(individualPlan);
    }
    if (mode === "Clone" || mode === "Edit") {
      const individualPlan = plans.find(
        (plan) => plan.esopPlanId === grant?.planid
      );
      formik.setFieldValue("planName", individualPlan?.planName);
      formik.setFieldValue("planType", individualPlan?.planType);
      setSelectedPlan(individualPlan);
    }
  }, [mode, _id]);
  useEffect(() => {
    if (grantByAmount) {
      formik.setFieldValue(
        "optionsGranted",
        Math.round(amountGranted / sharePrice)
      );
    }
  }, [amountGranted, grantByAmount, sharePrice]);

  useEffect(() => {
    handleGrantDateChange(formik.values.dateOfGrant);
  }, [formik.values.dateOfGrant]);

  useEffect(() => {
    handleVestingDateTypeChange(formik.values.vestingDateType);
  }, [formik.values.vestingDateType]);

  const plansToShow = useMemo(() => {
    const selectedEmployee = employees.find(
      (employee) => employee.id === formik.values.employeeId
    );
    if (selectedEmployee)
      return (
        plans.filter((plan) =>
          canUserBeAssignedToThisPlan(
            plan.planType ?? "",
            selectedEmployee.employmentType?.toString() ?? "",
            companyData?.currency ?? "INR - ₹"
          )
        ) || []
      );
    return plans || [];
  }, [formik.values.employeeId]);

  const handleGrantDateChange = (date: string) => {
    if (formik.values.vestingDateType === VestingDateType.GRANT_DATE) {
      formik.setFieldValue("vestingDate", date);
    }
  };

  const handleVestingDateTypeChange = (type: string) => {
    if (type === VestingDateType.GRANT_DATE) {
      formik.setFieldValue("vestingDate", formik.values.dateOfGrant);
    }
    if (
      type === VestingDateType.EMPLOYEE_JOINING_DATE &&
      selectedEmployee?.dateOfJoin
    ) {
      formik.setFieldValue(
        "vestingDate",
        format(new Date(selectedEmployee.dateOfJoin), "yyyy-MM-dd")
      );
    }
  };

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e?.target?.files?.[0]) return;
    const file = e.target.files[0];
    const base64 = await convertToBase64(file);
    formik.setFieldValue("grantLetter", base64);
    formik.setFieldValue("customizedGrantLetter", true);
    if (
      file.type ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    )
      formik.setFieldValue("grantLetterType", "docx");
    else {
      formik.setFieldValue("grantLetterType", "pdf");
    }
  }
  function getHeaderText() {
    if (mode === "Edit") return "Edit Grant";
    else if (mode === "Clone") return "Clone Grant";
    else return "Create Grant";
  }
  function handleDelete() {
    formik.setFieldValue("grantLetter", null);
    formik.setFieldValue("customizedGrantLetter", false);
  }
  return (
    <div className="">
      <div className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between">
          {getHeaderText()}{" "}
          <span onClick={() => onClose()} className="cursor-pointer">
            X
          </span>
        </h6>
      </div>
      <VStack className="w-full px-10 py-7 gap-9">
        <HStack className="gap-8 ">
          <div className="flex-1">
            <Label
              className={`${
                isStrikePriceOptional ? "" : "required "
              }text-sm font-normal`}
            >
              Option Holder
            </Label>
            <SearchDropDown
              onChange={(e, value) => {
                formik.setFieldValue("employeeId", value?.id);
                formik.setFieldValue(
                  "employeeIdentificationString",
                  value?.employeeIdentificationString
                );
                formik.setFieldValue("employeeName", value?.employeeName);
                setSelectedEmployee(
                  employees.find((emp) => emp.id === value?.id)
                );
              }}
              placeholder="--Select--"
              options={employedEmployees}
              value={
                employedEmployees.filter(
                  (item) => item.id === formik.values.employeeId
                )[0]
              }
              className={""}
              getOptionLabel={(option) =>
                `${option.employeeName} (${option.employeeIdentificationString})`
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
            />
            {formik.touched.employeeId && formik.errors.employeeId && (
              <Error text={formik.errors.employeeId} />
            )}
          </div>
          <div className="flex-1">
            <Label className="required text-sm font-normal">Plan Name</Label>
            <Select
              disabled={mode === "Edit"}
              options={plansToShow}
              value={selectedPlan?.planName}
              name="planName"
              isPlaceHolderDisabled={false}
              valueGetter={(plan) => plan.planName}
              textGetter={(plan) => plan.planName}
              onChange={(e) => {
                formik.handleChange(e);
                const individualPlan = plans.find(
                  (plan) => plan.planName === e.target.value
                );
                formik.setFieldValue("planName", individualPlan?.planName);
                formik.setFieldValue("planType", individualPlan?.planType);
                setSelectedPlan(individualPlan);
              }}
            />
            {formik.touched.planName && formik.errors.planName && (
              <Error text={formik.errors.planName} />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <Label
                className={`required text-sm font-normal ${
                  grantByAmount && "text-gray-300"
                }`}
              >
                No of Options
              </Label>
              {selectedPlan && (
                <Label className="text-xs font-normal text-green-400">
                  {getFormattedValue(
                    selectedPlan?.optionsReserved,
                    currency,
                    selectedPlan.isFractional
                  )}{" "}
                  options available
                </Label>
              )}
            </div>
            <Input
              max={allowedOptions}
              disabled={grantByAmount}
              type="number"
              {...formik.getFieldProps("optionsGranted")}
            />
            {formik.touched.optionsGranted && formik.errors.optionsGranted && (
              <Error text={formik.errors.optionsGranted} />
            )}
          </div>
        </HStack>
        <HStack className="gap-8">
          <div className="flex-1">
            <Label className="required text-sm font-normal">
              Grant Price ({currencySymbol})
            </Label>
            <Input type="number" {...formik.getFieldProps("grantPrice")} />
            {formik.touched.grantPrice && formik.errors.grantPrice && (
              <Error text={formik.errors.grantPrice} />
            )}
          </div>
          <div className="flex-1">
            <Label className="required text-sm font-normal">Grant Date</Label>
            <Input
              type="date"
              name="dateOfGrant"
              value={formik.values.dateOfGrant}
              onBlur={formik.handleBlur}
              onChange={(e) => {
                if (!e.target.value) return;
                formik.handleChange(e);
              }}
            />
            {formik.touched.dateOfGrant && formik.errors.dateOfGrant && (
              <Error text={formik.errors.dateOfGrant} />
            )}
          </div>
          <div className="flex-1 max-w-[30%]">
            <Label className="block text-sm font-normal">
              Custom Grant Letter
            </Label>
            <div className="py-1.5">
              {formik.values.customizedGrantLetter && mode !== "Clone" ? (
                <>
                  <div className="flex flex-row items-center justify-between cursor-pointer align-center">
                    <div
                      className="text-red-500 underline "
                      onClick={() => setDocumentDialog({ open: true, grant })}
                    >
                      Grant Letter.docx
                    </div>
                    <button
                      onClick={() => handleDelete()}
                      className="ml-6 text-zinc-300 hover:scale-105"
                    >
                      <DeleteIcon />
                    </button>
                    <FileInput
                      accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      file={formik.values.grantLetterFileName || "Replace"}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "grantLetterFileName",
                          e.target.value
                        );
                        handleFileUpload(e);
                      }}
                    />
                  </div>
                </>
              ) : (
                <FileInput
                  accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  file={formik.values.grantLetterFileName || "Attach File"}
                  onChange={(e) => {
                    formik.setFieldValue("grantLetterFileName", e.target.value);
                    handleFileUpload(e);
                  }}
                />
              )}
            </div>
          </div>
        </HStack>
        <Dialog
          open={documentDialog.open}
          onClose={() => setDocumentDialog({ open: false })}
          maxWidth="lg"
        >
          <LazyPdfDocument url={documentDialog?.grant?.grantLetterLink ?? ""} />
        </Dialog>
        <HStack className="gap-8">
          <VStack className="w-1/3 gap-8">
            <div>
              <Label className="required text-sm font-normal">
                Vesting Schedule
              </Label>
              <Select
                disabled={mode === "Edit"}
                options={vestingTemplateNames}
                isPlaceHolderDisabled={true}
                {...formik.getFieldProps("vestingTemplate")}
              />
              {formik.touched.vestingTemplate &&
                formik.errors.vestingTemplate && (
                  <Error text={formik.errors.vestingTemplate} />
                )}
            </div>
            <div>
              <Label className="text-sm font-normal">Vesting Date Type</Label>
              <Select
                options={vestingTypes}
                value={formik.values.vestingDateType}
                textGetter={(option) => option?.description}
                valueGetter={(value) => value?.value}
                isPlaceHolderDisabled={true}
                onChange={(option) => {
                  formik.setFieldValue(
                    "vestingDateType",
                    vestingTypes.find((v) => v.value === option?.target.value)
                      ?.value
                  );
                }}
              />
            </div>
            <div>
              <Label className="text-sm font-normal">Actual Vesting Day</Label>
              <Select
                options={availableActualVestingDays || []}
                value={formik.values.actualVestingDay}
                isPlaceHolderDisabled={true}
                textGetter={(option) => option.split("_").join(" ")}
                valueGetter={(value) => value}
                onChange={(option) => {
                  formik.setFieldValue(
                    "actualVestingDay",
                    option?.target.value
                  );
                }}
              />
            </div>
            <div>
              <Label
                className={`required text-sm font-normal ${
                  formik.values.vestingDateType !==
                    VestingDateType.CUSTOM_DATE && "text-gray-400"
                }`}
              >
                Select a Date
              </Label>
              <Input
                type="date"
                disabled={
                  formik.values.vestingDateType !== VestingDateType.CUSTOM_DATE
                }
                name="vestingDate"
                value={formik.values.vestingDate}
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  if (!e.target.value) return;
                  formik.handleChange(e);
                }}
              />
              {formik.touched.vestingDate && formik.errors.vestingDate && (
                <Error text={formik.errors.vestingDate} />
              )}
            </div>
          </VStack>
          <HStack className="items-center -mb-10 grow">
            <ProjectionChart
              templateName={formik.values.vestingTemplate}
              optionsGranted={formik.values.optionsGranted}
              vestingStartDate={new Date(formik.values.vestingDate)}
              actualVestingDay={formik.values.actualVestingDay}
            />
          </HStack>
        </HStack>
        <CustomFieldsSection
          entityType={UdfEntityType.GRANT}
          values={customFieldValues}
          onChange={setCustomFieldValues}
        />
        <HStack className="justify-between my-10 justify">
          <ButtonPrimary1 onClick={() => onClose()}>Back</ButtonPrimary1>
          <ButtonSecondary
            onClick={() => {
              formik.resetForm();
            }}
          >
            Clear
          </ButtonSecondary>
          <div className="flex items-center">
            <ButtonPrimary
              className={`${!formik.isValid && "bg-gray-400"}`}
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                formik.handleSubmit();
              }}
              loading={isLoading || editLoading}
            >
              Save & Continue
            </ButtonPrimary>
          </div>
        </HStack>
      </VStack>
      <AlertDialog
        open={
          showDialog &&
          (addGrantStatus === "success" || editGrantStatus === "success")
        }
        message="Grant Uploaded successfully"
        primaryActionText="Create a New Grant"
        secondaryActionText="Ok Got it!"
        onPrimaryAction={() => setShowDialog(false)}
        onSecondaryAction={() => setShowDialog(false)}
        onClose={() => setShowDialog(false)}
      />
      <AlertDialog
        open={
          showDialog &&
          (addGrantStatus === "error" || editGrantStatus === "error")
        }
        error
        message={errorMessage.message}
        primaryActionText="Retry"
        secondaryActionText="Go Back"
        onPrimaryAction={() => {
          errorMessage.reset();
          setShowDialog(false);
        }}
        onSecondaryAction={() => {
          errorMessage.reset();
          setShowDialog(false);
        }}
        onClose={() => {
          errorMessage.reset();
          setShowDialog(false);
        }}
      />
    </div>
  );
}

function ProjectionChart({
  templateName,
  optionsGranted = 0,
  vestingStartDate = new Date(new Date().getFullYear(), 0, 1),
  actualVestingDay = "SAME_DAY",
}: {
  templateName: string;
  optionsGranted?: number;
  vestingStartDate?: Date;
  actualVestingDay?: string;
}) {
  const { data: _templates } = useVestingTemplates();
  const templates = _templates || [];
  const vestingTemplates = templates?.filter((schedule) => !schedule.isDefault);
  const template = vestingTemplates.find(
    (template) => template.vestingTemplateName === templateName
  );
  template?.schedules.forEach((t) => {
    t.percentage /= 100;
  });
  const isFractional = vestingTemplates.some(
    (template) => template.isFractional
  );
  const vestings = generateProjections({
    cliffPeriod: template?.cliffPeriod ?? 12,
    optionsGranted,
    schedules: template?.schedules || [],
    vestingStartDate,
    vestingType: template?.vestingType || VestingType.STANDARD,
    isFractional,
  });
  vestings.forEach((v) => {
    if (!v.cliffDate) {
      v.date = getActualVestingDay(v.date, actualVestingDay);
    }
  });
  const data: ChartData<"bar", number[], unknown> = {
    labels: vestings.map((v) => format(v.date, "MMM yy")),
    datasets: [
      {
        label: "to date",
        data: vestings.map((v) =>
          isFractional
            ? parseFloat(
                (v.accumulatedVestedOptionsForGrant - v.vestedOptions).toFixed(
                  4
                )
              )
            : v.accumulatedVestedOptionsForGrant - v.vestedOptions
        ),
        backgroundColor: "#F8B195",
      },
      {
        label: "on date",
        data: vestings.map((v) =>
          isFractional
            ? parseFloat(v.vestedOptions.toFixed(4))
            : v.vestedOptions
        ),
        backgroundColor: "#F67280",
        borderRadius: 4,
      },
    ],
  };

  return <BarChart data={data} isFractional={isFractional} />;
}
